import type { AssetSummary, RevisionSummary } from '../api/types'

export interface DeletionAsset extends AssetSummary {
  row_version?: number
  status?: string
  folder_id?: string | null
}

export type DeleteTarget =
  | { kind: 'asset'; asset: DeletionAsset }
  | { kind: 'revision' | 'baseline'; revision: RevisionSummary }

type Inspection = { kind: 'ready' | 'changed' | 'pending'; target: DeleteTarget; requestId?: string | null }
const versionOf = (target: DeleteTarget) => {
  const version = target.kind === 'asset' ? target.asset.row_version : target.revision.row_version
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) throw new Error('未取得最新版本信息，请刷新后重试。')
  return version
}

/** A version bump alone can be refreshed; changes to the deletion scope need another confirmation. */
export function inspectDeletion(original: DeleteTarget, latest: DeleteTarget): Inspection {
  if (original.kind !== latest.kind) throw new Error('删除目标已变化，请重新选择。')
  versionOf(latest)
  if (original.kind === 'asset' && latest.kind === 'asset') {
    const before = original.asset, after = latest.asset
    if (before.id !== after.id) throw new Error('删除目标已变化，请重新选择。')
    if (after.status === 'pending_deletion' || after.archive_request_status === 'pending') return { kind: 'pending', target: latest, requestId: after.archive_request_id }
    if (after.status && after.status !== 'active') throw new Error('此文件已删除或停用，请刷新内容库。')
    const scope = (asset: DeletionAsset) => [asset.name, asset.current_revision_id, asset.folder_id ?? null, asset.classification, asset.reuse_policy, asset.latest_upload?.id ?? null]
    return { kind: JSON.stringify(scope(before)) === JSON.stringify(scope(after)) ? 'ready' : 'changed', target: latest }
  }
  if (original.kind !== 'asset' && latest.kind !== 'asset') {
    const before = original.revision, after = latest.revision
    if (before.id !== after.id || before.asset_id !== after.asset_id) throw new Error('删除目标已变化，请重新选择。')
    if (after.archive_request_status === 'pending') return { kind: 'pending', target: latest, requestId: after.archive_request_id }
    if (['archived','deleted'].includes(after.status)) throw new Error('此版本已删除，请刷新版本列表。')
    const scope = (revision: RevisionSummary) => [revision.status, revision.source_file_name ?? null, revision.page_count, revision.base_revision_id ?? null]
    return { kind: JSON.stringify(scope(before)) === JSON.stringify(scope(after)) ? 'ready' : 'changed', target: latest }
  }
  throw new Error('删除目标已变化，请重新选择。')
}

export function deletionRequest(target: DeleteTarget) {
  const expected_version = versionOf(target)
  return target.kind === 'asset'
    ? { path: `/assets/${encodeURIComponent(target.asset.id)}`, body: { expected_version, reason: 'user_request' } }
    : { path: `/assets/${encodeURIComponent(target.revision.asset_id)}/revisions/${encodeURIComponent(target.revision.id)}`, body: { expected_version, reason: 'user_request', baseline_only: target.kind === 'baseline' } }
}
